// Underline tabs inside a figure (DESIGN_SPEC §10.6), e.g. Network: Hosts · HTTP · DNS · Connections · Flows.
//   <Tabs label tabs value onChange idBase? morph? className? />
//     tabs: { id, label, count? }[] — a tab with count 0 keeps its place; mono 11.5/500, ink-mute → ink, 2px accent rail on the selected tab
//     role=tablist; ← → Home End move focus and select (automatic activation); the row wraps and never clips
//     morph (default true): the rail slides between tabs with a View Transition (off under reduced motion)
//   tabPanelProps(idBase, id) → { id, role: "tabpanel", "aria-labelledby", tabIndex } for the panel element
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { viewTransition } from "@/lib/motion";
import { cx } from "./cx";

export type TabItem<T extends string> = { id: T; label: ReactNode; count?: ReactNode };

const tabId = (idBase: string, id: string) => `${idBase}-tab-${id}`;
export const tabPanelProps = (idBase: string, id: string) => ({
  id: `${idBase}-panel-${id}`,
  role: "tabpanel" as const,
  "aria-labelledby": tabId(idBase, id),
  tabIndex: 0,
});

export function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  idBase,
  morph = true,
  className,
}: {
  label: string;
  tabs: readonly TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  idBase?: string;
  morph?: boolean;
  className?: string;
}) {
  const auto = useId();
  const base = idBase ?? `tabs${auto.replace(/[^a-zA-Z0-9]/g, "")}`;
  const rail = `tab-rail-${base.replace(/[^a-zA-Z0-9-]/g, "")}`;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = Math.max(0, tabs.findIndex((t) => t.id === value));

  const select = (i: number) => {
    const t = tabs[i];
    if (!t || t.id === value) return;
    if (morph) viewTransition(() => onChange(t.id));
    else onChange(t.id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = tabs.length;
    if (!n) return;
    let next: number | undefined;
    if (e.key === "ArrowRight") next = (current + 1) % n;
    else if (e.key === "ArrowLeft") next = (current - 1 + n) % n;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    if (next === undefined) return;
    e.preventDefault();
    select(next);
    refs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className={cx("flex flex-wrap gap-x-5 border-b border-rule", className)} onKeyDown={onKeyDown}>
      {tabs.map((t, i) => {
        const on = i === current;
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={tabId(base, t.id)}
            aria-controls={`${base}-panel-${t.id}`}
            aria-selected={on}
            aria-current={on ? "true" : undefined}
            tabIndex={on ? 0 : -1}
            className={cx("fi-tab py-2", on && morph && "border-b-transparent")}
            onClick={() => select(i)}
          >
            {t.label}
            {t.count !== undefined && t.count !== null && <span className="ct">{t.count}</span>}
            {on && morph && <span aria-hidden="true" className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-accent" style={{ viewTransitionName: rail }} />}
          </button>
        );
      })}
    </div>
  );
}
